/**
 * AgentSuggestionPill — an answer's proposal of another persona to lead the conversation (the `agent_suggestion` part,
 * from the `agent-suggestion` output contract; conversation-members spec). It names the persona (resolved by the engine
 * from its id) and why, and offers what the person can do: bring it into the conversation — then "To" or "@" asks it —
 * or, for the conversation's owner, make it the lead. The agent never switches itself.
 * Demo page: /chats (a suggestion chip under a message).
 */
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, Sparkles, UserPlus } from 'lucide-react';
import {
  inviteToConversationMutation,
  listConversationMembersQueryKey,
  setConversationLeadMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { useConversationMembers } from '../../hooks/useConversationMembers.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { useToast } from '../ui/Toast.js';

type AgentSuggestionPart = Extract<ConversationMessagePart, { type: 'agent_suggestion' }>;

interface AgentSuggestionPillProps {
  conversationId: string;
  part: AgentSuggestionPart;
}

export function AgentSuggestionPill({ conversationId, part }: AgentSuggestionPillProps) {
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const canManageAgents = usePolicy('CanManageAgents');
  const { members, me } = useConversationMembers(conversationId, []);
  const member = members.find((m) => m.memberType === 'persona' && m.memberId === part.personaId) ?? null;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: listConversationMembersQueryKey({ path: { id: conversationId } }) });
  const invite = useMutation({ ...inviteToConversationMutation(), onSuccess: () => void refresh() });
  const setLead = useMutation({ ...setConversationLeadMutation(), onSuccess: () => void refresh() });
  const busy = invite.isPending || setLead.isPending;

  const add = () =>
    invite.mutateAsync({ path: { id: conversationId }, body: { memberType: 'persona', memberId: part.personaId } });

  const join = () =>
    add().then(
      () => notify(`${part.name} joined. Pick it in "To" or type @ to ask it.`, 'success'),
      (error: Error) => notify(error.message || `Could not add ${part.name}.`, 'danger'),
    );

  const lead = async () => {
    try {
      const target = member ?? (await add());
      await setLead.mutateAsync({ path: { id: conversationId, memberId: String(target.id) } });
      notify(`${part.name} leads this conversation now.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : `Could not make ${part.name} the lead.`, 'danger');
    }
  };

  const isOwner = me?.role === 'owner';
  return (
    <div className="flex max-w-md items-start gap-2 rounded-lg border bg-surface-alt px-3 py-2 text-sm">
      <Sparkles size={14} className="mt-0.5 shrink-0 text-accent-text" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-heading">Suggested: {part.name}</div>
        <div className="mt-0.5 text-xs text-muted">{part.reason}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {member?.role === 'lead' ? (
            <Badge variant="info">Leads this conversation</Badge>
          ) : (
            <>
              {!member && canManageAgents && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  loading={invite.isPending}
                  onClick={() => void join()}
                >
                  <UserPlus size={12} aria-hidden />
                  Add to conversation
                </Button>
              )}
              {isOwner && (member || canManageAgents) && (
                <Button size="sm" disabled={busy} loading={setLead.isPending} onClick={() => void lead()}>
                  <Crown size={12} aria-hidden />
                  Make lead
                </Button>
              )}
              {member && !isOwner && (
                <span className="text-xs text-muted">In the conversation: ask it with "To" or @.</span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
